import { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { logger } from "../services/telemetry";
import { AuthSplash } from "./AuthSplash";
import { applyEmailVerificationCode } from "./firebase";
import { AUTH_ENABLED, firebaseConfig } from "./config";
import { CHECK_EMAIL_PATH } from "./paths";

/* The /auth/action route — the Firebase email-action handler this app hosts
 * now that studio's (the shared project's previous action URL) is sunset. The
 * console's template action URL points here; Firebase appends mode + oobCode
 * (+ continueUrl on verification links, set by the identity service).
 *
 * Only verifyEmail is handled in-app: apply the code, then hop to the
 * continueUrl. Every other mode (resetPassword, recoverEmail, future ones) is
 * forwarded — query intact — to Firebase's own hosted handler at
 * /__/auth/action on the auth domain, which owned them before this route
 * existed and still does. */

/** Where a just-verified user lands when the link carries no usable
 *  continueUrl — the first-run wizard (matches the identity service's
 *  tokenwise continueUrl). */
const VERIFIED_FALLBACK_PATH = "/welcome";

/** Router-relative target from the emailed continueUrl. Only same-origin
 *  absolute URLs are honored (the identity service sends the deployment's own
 *  host) so the param can't be used as an open redirect; anything foreign,
 *  relative, or unparseable falls back to the wizard. */
function verifiedTarget(continueUrl: string | null): string {
  if (!continueUrl) return VERIFIED_FALLBACK_PATH;

  let url: URL;
  try {
    url = new URL(continueUrl);
  } catch {
    return VERIFIED_FALLBACK_PATH;
  }
  if (url.origin !== window.location.origin) return VERIFIED_FALLBACK_PATH;

  return url.pathname + url.search + url.hash;
}

export function AuthActionRoute() {
  // No auth gate ⇒ no Firebase project to act against; nothing to handle.
  if (!AUTH_ENABLED) return <Navigate to="/" replace />;

  return <AuthActionHandler />;
}

function AuthActionHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  // StrictMode double-mount guard: an oobCode is single-use, so the second
  // invocation must not re-apply (it would fail and misroute to /check-email).
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;

    handled.current = true;

    const params = new URLSearchParams(location.search);

    if (params.get("mode") !== "verifyEmail") {
      // Full browser hop: Firebase's hosted handler is not an SPA route.
      window.location.replace(
        `https://${firebaseConfig.authDomain}/__/auth/action${location.search}`,
      );
      return;
    }

    const oobCode = params.get("oobCode");
    const next = verifiedTarget(params.get("continueUrl"));

    if (!oobCode) {
      navigate(CHECK_EMAIL_PATH, { replace: true });
      return;
    }

    applyEmailVerificationCode(oobCode).then(
      () => navigate(next, { replace: true }),
      (err: unknown) => {
        // Expired/used codes land here; /check-email offers a resend (or, if
        // this browser's session is in fact verified already, forwards on).
        logger.warn(
          err instanceof Error ? err : new Error(String(err)),
          "email verification action failed",
        );
        navigate(CHECK_EMAIL_PATH, { replace: true });
      },
    );
  }, [location.search, navigate]);

  return <AuthSplash />;
}
